import { useEffect, useId, useRef, useState } from "react";
import { Button } from "../../../components/ui/Button";
import { useT } from "../../../i18n";
import { SegGroup } from "../../../components/ui/Seg";
import { CmdRejected } from "../api/consoleApi";
import { usePointerDrag } from "../hooks/usePointerDrag";
import type { ConsoleApi } from "../api/consoleApi";
import type { ConsoleRoom, ConsoleStudent, TeamDraft, TeamKind, TeamSize } from "../types";
import {
  canJoin,
  cargoOf,
  clear,
  initialDraft,
  move,
  payload,
  prune,
  randomize,
  resize,
  sameDraft,
  TEAM_SIZES,
  unassigned,
} from "../lib/teams";

/** 끌고 있는 곳 — 슬롯 번호 또는 미배정 칸 */
type Target = number | "pool";

/**
 * FS-11-03 매칭·팀 편성(SCR v0.6 sfs-11-03) — 협동 운반 = 팀(크기 2/3/4) · 밀어내기 = 1:1 고정 쌍.
 *  - 학생 칩을 끌어 팀 슬롯/미배정 칸에 놓는다 · 키보드: 칩을 눌러 고르고(1) 옮길 곳의 ＋ 를 누른다(2) · Esc 취소
 *  - 🎲 무작위 편성 · ↺ 초기화 · ✓ 적용(POST …/teacher/room/teams) — 끊긴·분리 학생은 자동으로 미배정(보내지 않는다)
 *  - 상태: 편성 중 · 적용됨 · 점수·순위 열 없음(DR-01)
 */
export function TeamsPanel({
  room,
  students,
  api,
  onApplied,
  onError,
  onDone,
}: {
  room: ConsoleRoom;
  students: ConsoleStudent[];
  api: ConsoleApi;
  onApplied: () => Promise<void> | void;
  onError: (e: unknown) => void;
  onDone: (text: string) => void;
}) {
  const t = useT();
  const sizeId = useId();
  const kind: TeamKind | null =
    room.modeId === "carry-together" ? "teams" : room.modeId === "pushout-ring" ? "pairs" : null;
  const [draft, setDraft] = useState<TeamDraft | null>(null);
  const [applied, setApplied] = useState<TeamDraft | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const [live, setLive] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const studentsRef = useRef(students);
  studentsRef.current = students;
  const ready = students.length > 0;

  // 모드가 바뀌면(또는 처음) 서버가 쓰는 편성으로 새 초안
  useEffect(() => {
    if (!kind || !ready) {
      setDraft(null);
      return;
    }
    const d = initialDraft(studentsRef.current, kind);
    setDraft(d);
    setApplied(d.slots.some((g) => g.length) ? d : null);
    setPicked(null);
    setProblem(null);
  }, [kind, ready]);

  // 끊기거나 분리된 학생은 자동으로 미배정 큐로(매 읽기마다)
  useEffect(() => setDraft((d) => (d ? prune(d, students) : d)), [students]);

  const byPid = new Map(students.map((s) => [s.pid, s]));
  const eligible = students.filter(canJoin).length;
  const nameOf = (pid: string) => byPid.get(pid)?.nickname ?? pid;
  const slotName = (i: number) =>
    t(kind === "pairs" ? "teacher.teams.pair" : "teacher.teams.team", { n: i + 1 });

  const edit = (next: TeamDraft) => {
    setDraft(next);
    setProblem(null);
  };
  const place = (pid: string, to: number | null) => {
    if (!draft) return;
    const next = move(draft, pid, to);
    if (next === draft) return;
    edit(next);
    setLive(
      t("teacher.teams.moved", {
        name: nameOf(pid),
        to: to === null ? t("teacher.teams.toUnassigned") : slotName(to),
      }),
    );
    setPicked(null);
  };
  const pick = (pid: string) => {
    const s = byPid.get(pid);
    if (!s || !canJoin(s)) return;
    if (picked === pid) {
      setPicked(null);
      return;
    }
    setPicked(pid);
    setLive(t("teacher.teams.picked", { name: s.nickname }));
  };

  const apply = async () => {
    if (!draft) return;
    const p = payload(draft);
    if (!p.ok) {
      setProblem(
        p.reason === "none"
          ? t("teacher.teams.none")
          : t(draft.kind === "pairs" ? "teacher.teams.smallPairs" : "teacher.teams.small"),
      );
      return;
    }
    setSending(true);
    try {
      await api.teams(p.body);
      setApplied(draft);
      onDone(t("teacher.teams.applied"));
      await onApplied();
    } catch (e) {
      if (e instanceof CmdRejected && e.field === "mode")
        setProblem(t("teacher.teams.modeMismatch"));
      else onError(e);
    } finally {
      setSending(false);
    }
  };

  // ── 끌어 놓기(포인터 — 마우스 · 터치 · 펜 · Firefox 모두 · usePointerDrag) ──
  const dnd = usePointerDrag<Target>((pid, target) =>
    place(pid, target === "pool" ? null : target),
  );
  const over = dnd.over;

  const chip = (s: ConsoleStudent) => {
    const ok = canJoin(s);
    const why =
      s.connection === "dropped"
        ? t("teacher.teams.dropped")
        : s.connection === "isolated"
          ? t("teacher.teams.isolated")
          : s.connection === "offline"
            ? t("teacher.teams.offline")
            : null;
    return (
      <button
        key={s.pid}
        type="button"
        className="pf-btn pf-btn--ghost pf-btn--sm pf-tchip"
        aria-pressed={ok ? picked === s.pid : undefined}
        aria-disabled={ok ? undefined : "true"}
        aria-label={ok ? t("teacher.teams.pick", { name: s.nickname }) : undefined}
        data-pid={s.pid}
        data-testid="team-chip"
        onClick={() => pick(s.pid)}
        {...(ok ? dnd.source(s.pid, s.nickname) : {})}
      >
        {s.connection === "dropped" && <span aria-hidden="true">🔴</span>}
        {s.nickname}
        {why && ` · ${why}`}
      </button>
    );
  };

  const status = draft && applied && sameDraft(draft, applied) ? "applied" : "editing";
  const pool = draft ? unassigned(draft, students) : [];
  const pickedIn = picked && draft ? draft.slots.findIndex((g) => g.includes(picked)) : -1;
  const short =
    draft?.kind === "teams"
      ? draft.slots.map((g, i) => ({ i, n: g.length })).filter((x) => x.n > 0 && x.n < draft.size)
      : [];

  return (
    <div
      className="pf-teams"
      onKeyDown={(e) => {
        if (e.key === "Escape") setPicked(null);
      }}
    >
      <section
        className="pf-pane pf-pane--hero"
        aria-labelledby="tm-title"
        data-testid="fs-11-03-head"
        data-state={status}
      >
        <div className="pf-teams__head">
          <h2 id="tm-title" className="pf-h3">
            {t("teacher.teams.method")}
          </h2>
          <div className="pf-state" data-testid="tm-kind">
            <span
              className={`pf-state__pill${kind === "pairs" ? " pf-state__pill--on" : ""}`}
              aria-current={kind === "pairs" ? "true" : undefined}
            >
              {t("teacher.teams.kindPairs")}
            </span>
            <span
              className={`pf-state__pill${kind === "teams" ? " pf-state__pill--on" : ""}`}
              aria-current={kind === "teams" ? "true" : undefined}
            >
              {t("teacher.teams.kindTeams")}
            </span>
          </div>
          {draft?.kind === "teams" && (
            <SegGroup label={t("teacher.teams.size")} variant="green" testId="tm-size">
              {TEAM_SIZES.map((n) => (
                <label key={n} className="pf-seg__opt">
                  <input
                    type="radio"
                    name={`${sizeId}-size`}
                    value={n}
                    checked={draft.size === n}
                    onChange={() => edit(resize(draft, n as TeamSize, eligible))}
                  />
                  <span>{n}</span>
                </label>
              ))}
            </SegGroup>
          )}
          {draft && (
            <div className="pf-row pf-rc__end">
              <Button
                variant="ghost"
                className="pf-btn--sm"
                data-testid="tm-random"
                onClick={() => edit(randomize(draft, students))}
              >
                {t("teacher.teams.random")}
              </Button>
              <Button
                variant="ghost"
                className="pf-btn--sm"
                data-testid="tm-reset"
                onClick={() => edit(clear(draft, eligible))}
              >
                {t("teacher.teams.reset")}
              </Button>
              <Button
                variant="accent"
                className="pf-btn--sm"
                disabled={sending}
                data-testid="tm-apply"
                onClick={() => void apply()}
              >
                {t("teacher.teams.apply")}
              </Button>
            </div>
          )}
        </div>
        {kind ? (
          <p className="pf-muted">
            {t("teacher.teams.hint")} ·{" "}
            <span
              className={`pf-chip-static pf-teams__status ${status === "applied" ? "pf-chip-static--ok" : "pf-chip-static--warn"}`}
              data-testid="tm-status"
            >
              {t(`teacher.teams.state.${status}`)}
            </span>
          </p>
        ) : (
          <div className="pf-banner pf-banner--info" role="note" data-testid="tm-lobby">
            <div className="pf-banner__text">{t("teacher.teams.lobby")}</div>
          </div>
        )}
        {problem && (
          <p className="pf-field-err" role="alert" data-testid="tm-problem">
            {problem}
          </p>
        )}
        <p className="pf-sr-only" aria-live="polite">
          {live}
        </p>
      </section>

      {draft && (
        <div className="pf-teams__body">
          <section
            className={`pf-pane pf-teams__pool${over === "pool" ? " pf-teams__pool--target" : ""}`}
            aria-labelledby="tm-pool"
            data-testid="tm-pool"
            {...dnd.target("pool")}
          >
            <h2 id="tm-pool" className="pf-h3">
              {t("teacher.teams.unassigned", { n: pool.length })}
            </h2>
            {pickedIn >= 0 && (
              <Button
                variant="ghost"
                className="pf-btn--sm pf-tchip--add"
                data-testid="tm-to-pool"
                onClick={() => place(picked!, null)}
              >
                {t("teacher.teams.toUnassigned")}
              </Button>
            )}
            <div className="pf-teams__list">{pool.map(chip)}</div>
            <p className="pf-teams__note pf-muted">{t("teacher.teams.pairsNote")}</p>
          </section>

          <section className="pf-pane" aria-labelledby="tm-slots">
            <h2 id="tm-slots" className="pf-h3">
              {t(draft.kind === "pairs" ? "teacher.teams.slotsPairs" : "teacher.teams.slots")}
            </h2>
            <div className="pf-teams__slots" data-testid="tm-slots">
              {draft.slots.map((g, i) => (
                <div
                  key={i}
                  className={`pf-team${over === i ? " pf-team--target" : ""}`}
                  role="group"
                  aria-label={slotName(i)}
                  data-slot={i}
                  data-testid="tm-slot"
                  {...dnd.target(i)}
                >
                  <div className="pf-team__head">
                    <b>{slotName(i)}</b>
                    {draft.kind === "teams" && (
                      <span className="pf-chip-static pf-chip-static--info">
                        {t("teacher.teams.cargoChip", {
                          cargo: t(`teacher.teams.cargo.${cargoOf(draft.size)}`),
                          n: draft.size,
                        })}
                      </span>
                    )}
                    <span className="pf-muted pf-team__count">
                      {t("teacher.teams.count", { n: g.length, size: draft.size })}
                    </span>
                  </div>
                  <div className="pf-team__members">
                    {g.map((pid) => {
                      const s = byPid.get(pid);
                      return s ? chip(s) : null;
                    })}
                    {g.length < draft.size && (
                      <button
                        type="button"
                        className="pf-btn pf-btn--ghost pf-btn--sm pf-tchip pf-tchip--add"
                        disabled={!picked}
                        aria-label={t("teacher.teams.addTo", { slot: slotName(i) })}
                        data-testid="tm-add"
                        onClick={() => picked && place(picked, i)}
                      >
                        ＋
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {draft.kind === "teams" && (
              <div className="pf-banner pf-banner--info" role="note" data-testid="tm-cargo">
                <div className="pf-banner__text">
                  {t("teacher.teams.cargoNote")}{" "}
                  {short.map((x) => (
                    <span key={x.i}>
                      {t("teacher.teams.cargoDown", {
                        team: slotName(x.i),
                        n: x.n,
                        cargo: t(`teacher.teams.cargo.${cargoOf(x.n)}`),
                      })}{" "}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <p className="pf-muted">{t("teacher.teams.keyboardHint")}</p>
          </section>
        </div>
      )}
      {dnd.ghost}
    </div>
  );
}
